import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

export const GeometricBackground = ({ className = '' }) => {
  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-circuit-pattern opacity-30" />
      
      {/* Floating Circles */}
      <motion.div
        className="absolute top-20 right-[10%] w-64 h-64 rounded-full border border-primary/10"
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 90, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear',
        }}
      />
      <motion.div
        className="absolute top-40 right-[15%] w-48 h-48 rounded-full border border-accent/10"
        animate={{
          scale: [1.1, 1, 1.1],
          rotate: [0, -90, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'linear',
        }}
      />
      
      {/* Diagonal Lines */}
      <div className="absolute top-0 left-0 w-full h-full">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="diagonalGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.05" />
              <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <line x1="0" y1="100%" x2="50%" y2="0" stroke="url(#diagonalGradient)" strokeWidth="1" />
          <line x1="50%" y1="100%" x2="100%" y2="0" stroke="url(#diagonalGradient)" strokeWidth="1" />
        </svg>
      </div>

      {/* Gradient Orbs */}
      <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-gradient-radial from-primary/5 to-transparent blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-gradient-radial from-accent/5 to-transparent blur-3xl" />
    </div>
  );
};

export const ParticleField = ({ count = 30 }) => {
  const [particles, setParticles] = React.useState([]);

  React.useEffect(() => {
    const generatedParticles = Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 10 + 10,
      delay: Math.random() * 5,
    }));
    setParticles(generatedParticles);
  }, [count]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-primary/20"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
          }}
          animate={{
            y: [-20, 20, -20],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

export const HeroDecoration = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Animated circles */}
      <motion.div
        className="absolute top-1/4 right-[5%] w-72 h-72"
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
      >
        <div className="absolute inset-0 rounded-full border-2 border-dashed border-primary-foreground/10" />
        <div className="absolute inset-4 rounded-full border border-primary-foreground/10" />
        <div className="absolute inset-8 rounded-full border border-dashed border-accent/20" />
      </motion.div>

      {/* Floating squares */}
      <motion.div
        className="absolute bottom-1/4 left-[10%] w-16 h-16 border border-primary-foreground/10 rotate-45"
        animate={{
          y: [-10, 10, -10],
          rotate: [45, 55, 45],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute top-1/3 left-[20%] w-8 h-8 bg-accent/20 rotate-12"
        animate={{
          scale: [1, 1.2, 1],
          rotate: [12, 24, 12],
        }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Connection lines */}
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <motion.path
          d="M0,200 Q400,100 800,250 T1600,200"
          fill="none"
          stroke="hsl(var(--primary-foreground) / 0.05)"
          strokeWidth="1"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse' }}
        />
      </svg>
    </div>
  );
};
